import React, { useState, useEffect } from 'react';
import './App.css';
import Loader from './Loader';
import { getLivestreamDetails, getUserProfile, pingZoomSession, invalidateCacheZoomSession } from './service';
import { useLocation } from 'react-router-dom';
import { generateIV, encrypt } from './zoomEncryption';

import { useCookies } from 'react-cookie';

declare var ZoomMtg;

// temporary
ZoomMtg.setZoomJSLib('https://source.zoom.us/3.8.10/lib', '/av');

ZoomMtg.preLoadWasm();
ZoomMtg.prepareWebSDK();
// loads language files, also passes any error messages to the ui
ZoomMtg.i18n.load('en-US');
ZoomMtg.i18n.reload('en-US');

const redirectLoginURL = process.env.REACT_APP_NODE_ENV === 'production' ? 'https://login.pahamify.com/' : 'https://stg-login.pahamify.com/';
const ZOOM_PING_INTERVAL = 60000;

function App() {
    const [userToken, setUserToken] = useState('');
    const [livestreamId, setLivestreamId] = useState('');
    const [zoomCredential, setZoomCredential] = useState({
        userName: '',
        userEmail: '',
        passWord: '',
        meetingNumber: '',
        // apiKey: '',
        sdkKey: '',
        signature: '',
    });
    const [isLoading, setIsLoading] = useState(true);
    const [sdkVersion, setSdkVersion] = useState('3.8.10');
    const [isError, setIsError] = useState(false);
    const [errMessage, setErrMessage] = useState('');
    
    const [cookies] = useCookies(['vuex']);

    const location = useLocation();
    let pingInterval = null;
    
    function cleanupTheApp() {
        clearZoomPingInterval();
    }

    useEffect(() => {
        // Event listener for cleaning up the interval and remove the cookies
        window.addEventListener('beforeunload', cleanupTheApp);
        try {
            const { pathname } = location;
            if (!pathname) {
                setIsLoading(false);
            }
            const queryParams = new URLSearchParams(location.search);
            const tempLivestreamId = queryParams.get("livestreamId");
            
            if (!tempLivestreamId) throw new Error('Terjadi kesalahan pada server. Mohon coba ulang kembali. (Missing ID)');
            
            const checkSystemRequirements = ZoomMtg.checkSystemRequirements();
            const zoomSDKVersion = ZoomMtg.getWebSDKVersion();
            setSdkVersion(zoomSDKVersion[0] || '3.8.10');
            
            if (!cookies?.vuex) redirectToLogin();
            const { vuex } = cookies;
            
            if (!checkSystemRequirements) {
                throw new Error('Perangkat Anda tidak mendukung untuk menjalankan aplikasi zoom pada web browser Anda.');
            }
            
            setUserToken(`bearer ${vuex?.auth?.token}`);
            setLivestreamId(tempLivestreamId);
        } catch (e) {
            console.log("didMount useEffect error", e, e.message);
            setIsError(true);
            setErrMessage(e?.message || null);
            setIsLoading(false);
        }
        
        return () => window.removeEventListener('beforeunload', cleanupTheApp);
    }, []);

    useEffect(() => {
        if (userToken) getDatas();
    }, [userToken]);

    useEffect(() => {
        if (!zoomCredential.signature) return;

        checkBeforeInitMeeting();
    }, [zoomCredential.signature]);

    async function getDatas() {
        try {
            const [userProfile, livestream] = await Promise.all([getUserProfile(userToken), getLivestreamDetails(livestreamId, userToken)]);
            if (userProfile.statusCode === 401) redirectToLogin();
            if (!userProfile || !livestream?.GetLiveStreamByID?.MeetingID) {
                setIsLoading(false);
                return;
            };

            const { data } = userProfile;
            const { GetLiveStreamByID } = livestream;

            const ivKey = GetLiveStreamByID?.MeetingID ? generateIV(GetLiveStreamByID.MeetingID) : null;
            const encryptedUID = ivKey ? encrypt(data.UserId, ivKey) : null;
            const userName = encryptedUID ? `${data.NickName} | ${encryptedUID}` : data.NickName;
    
            setZoomCredential({
                userName,
                userEmail: data.Email,
                passWord: GetLiveStreamByID.MeetingPassword,
                meetingNumber: GetLiveStreamByID.MeetingID,
                // apiKey: process.env.REACT_APP_ZOOM_API_KEY,
                sdkKey: process.env.REACT_APP_SDK_KEY,
                signature: GetLiveStreamByID.Signature,
            });
        } catch (e) {
            console.log("getDatas() error check", e, e.message);
            setIsLoading(false);
        }
    }

    function startMeeting() {
        const zoomRoot = document.getElementById('zmmtg-root');
        zoomRoot.style.display = 'block';

        const leaveUrl = `${process.env.REACT_APP_DOMAIN}/ended`;

        ZoomMtg.init({
            debug: true,
            leaveUrl,
            isSupportAV: true,
            isSupportPolling: true,
            isSupportQA: true,
            showMeetingHeader: false, //option
            disableInvite: true, //optional
            disableRecord: true, //optional
            videoHeader: false, //optional,
            screenShare: false, //optional,
            meetingInfo: [ // optional
                'topic',
                'dc',
                'enctype',
                'report',
            ],
            success: (success) => {
                ZoomMtg.join({
                    signature: zoomCredential.signature,
                    meetingNumber: zoomCredential.meetingNumber,
                    userName: zoomCredential.userName,
                    // apiKey: zoomCredential.apiKey,
                    sdkKey: zoomCredential.sdkKey,
                    userEmail: zoomCredential.userEmail,
                    passWord: zoomCredential.passWord,
                    success: (success) => {
                        ZoomMtg.showInviteFunction({
                            show: false
                        });
                    },
                    error: (error) => {
                        console.log(error)
                        console.log("your sdk key is ",zoomCredential.sdkKey)
                    }
                })

            },
            error: (error) => {
                console.log(error)
            }
        })
    }
    
    async function getZoomPing() {
        try {
            const res = await pingZoomSession(`${livestreamId}`, userToken);
            if (res.statusCode === 403) {
                let err = new Error();
                err.status = res?.statusCode;
                throw err;
            }

            return true;
        } catch (e) {
            const statusCode = e?.status || 500;            
            if (statusCode === 403) {
                setErrMessage('Akun Zoom Kamu Masih Aktif di Perangkat Lain. Yuk logout (keluar) sesi Zoom di ponsel/device-mu yang lain dan masuk lagi ke sini!')
            }

            setIsError(true);
            setIsLoading(false);
            
            return false;
        }
    }
    
    // Ping interval
    function setZoomPingInterval() {
        pingInterval = setInterval(async () => {
            getZoomPing();
        }, ZOOM_PING_INTERVAL);
    }
    
    async function clearZoomPingInterval() {
        if (!pingInterval) return false;

        clearInterval(pingInterval);
        await invalidateCacheZoomSession(userToken);
    }
    /////////
    
    async function checkBeforeInitMeeting() {
        const pingResult = await getZoomPing();
        if (!pingResult) return false;
        
        setZoomPingInterval();
        startMeeting();
    }
    
    function redirectToLogin() {
        return window.location.assign(`${redirectLoginURL}?returnUrl=${window.location.href}`);
    }

    return (
        <div className="App">
            <main>
                <div className="content-container">
                    {
                        isLoading && (
                            <div className="loading">
                                <Loader />
                                <p>Harap tunggu...</p>
                            </div>
                        )
                    }
                    {
                        !isLoading && (
                            <div className="loading">
                                <p style={{ fontWeight: 700 }}>{isError && errMessage ? errMessage : 'Terjadi kesalahan pada server. Mohon coba ulang kembali.'}</p>
                                <p>Silahkan kembali ke aplikasi Pahamify 🙂</p>
                            </div>
                        )
                    }
                    <div className="version">
                        <p style={{ fontSize: 12, fontWeight: 700 }}>{`Zoom Web SDK v${sdkVersion}`}</p>
                    </div>
                </div>
            </main>
        </div>
    );
}

export default App;
